import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import type { Response } from "../interfaces";

export type TemplateEmailType =
  | "reminder"
  | "referral"
  | "transactional"
  | "occasional";

export type TemplateKey =
  | "reminder_1_giftbox_mailbox"
  | "reminder_1_giftbox_system"
  | "reminder_1_giftcard_mailbox"
  | "reminder_1_giftcard_system"
  | "reminder_2_giftbox_mailbox"
  | "reminder_2_giftbox_system"
  | "reminder_2_giftcard_mailbox"
  | "reminder_2_giftcard_system"
  | "referral_1_mailbox"
  | "referral_1_system"
  | "referral_2_mailbox"
  | "referral_2_system"
  | "shipping_giftbox_recipient_mailbox"
  | "shipping_giftbox_recipient_system"
  | "shipping_giftbox_sender_system"
  | "delivery_giftbox_recipient_mailbox"
  | "delivery_giftbox_recipient_system"
  | "delivery_giftbox_sender_system"
  | "acceptance_giftbox_recipient_mailbox"
  | "acceptance_giftbox_recipient_system"
  | "acceptance_giftbox_sender_system"
  | "acceptance_giftcard_recipient_mailbox"
  | "acceptance_giftcard_recipient_system"
  | "acceptance_giftcard_sender_system";

export interface SystemEmailTemplate {
  email_template_id: string;
  title: string;
  subject: string;
  body: string;
  text_message_body?: string | null;
  category?: string;
  email_type: TemplateEmailType | null;
  template_key: TemplateKey | null;
  is_active: boolean;
  is_delete?: boolean;
  added_on?: string;
  updated_at?: string;
}

export type CreateSystemEmailTemplatePayload = {
  title: string;
  subject: string;
  body: string;
  email_type: TemplateEmailType;
  template_key: TemplateKey;
  category?: string;
  is_active?: boolean;
  text_message_body?: string;
};

export const emailTemplateApi = createApi({
  reducerPath: "emailTemplateApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["SystemEmailTemplate"],
  endpoints: (builder) => ({
    getSystemEmailTemplates: builder.query<
      Response<SystemEmailTemplate[]>,
      { email_type?: TemplateEmailType; key_based?: boolean } | void
    >({
      query: (params) => ({
        url: "admin/email-templates",
        method: "GET",
        params: {
          key_based: params?.key_based ?? true,
          ...(params?.email_type ? { email_type: params.email_type } : {}),
        },
      }),
      providesTags: ["SystemEmailTemplate"],
    }),
    createSystemEmailTemplate: builder.mutation<
      Response<SystemEmailTemplate>,
      CreateSystemEmailTemplatePayload
    >({
      query: (data) => ({
        url: "admin/email-templates",
        method: "POST",
        data,
      }),
      invalidatesTags: ["SystemEmailTemplate"],
    }),
    updateSystemEmailTemplate: builder.mutation<
      Response<SystemEmailTemplate>,
      { id: string; data: Partial<CreateSystemEmailTemplatePayload> }
    >({
      query: ({ id, data }) => ({
        url: `admin/email-templates/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["SystemEmailTemplate"],
    }),
    deleteSystemEmailTemplate: builder.mutation<Response<void>, string>({
      query: (id) => ({
        url: `admin/email-templates/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SystemEmailTemplate"],
    }),
  }),
});

export const {
  useGetSystemEmailTemplatesQuery,
  useCreateSystemEmailTemplateMutation,
  useUpdateSystemEmailTemplateMutation,
  useDeleteSystemEmailTemplateMutation,
} = emailTemplateApi;
